// Create / edit a preset (custom template): content, {{placeholders}},
// display defaults, default lifetime and default audience — with a live
// English + Arabic preview at the end.
import React, { useMemo, useState } from "react";
import { Plus, Trash2, Save, LayoutTemplate, Info, FileText, Braces, SlidersHorizontal, Eye, Lightbulb } from "lucide-react";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import BroadcastCard from "../../../components/Broadcasts/BroadcastCard";
import { Modal, Field, Toggle, Btn, Select, Segmented, IconBtn, GroupTitle } from "./ui";
import { extractPlaceholders } from "../utils/render";
import { CATEGORIES, SEVERITIES, DISPLAY_MODES, VARIABLE_TYPES } from "../utils/labels";

const blank = {
  name: "",
  description: "",
  category: "GENERAL",
  titleEn: "",
  titleAr: "",
  bodyEn: "",
  bodyAr: "",
  variables: [],
  severity: "INFO",
  displayMode: "BANNER",
  dismissible: true,
  requireAck: false,
  addToInbox: false,
  defaultDurationHours: "",
  defaultAudienceId: "",
};

const TemplateEditorModal = ({ template, onClose, onSaved }) => {
  const { s, t, theme, screenSize, audiences, notify } = useBroadcastPage();
  const E = t.editor;
  const C = t.compose;
  const [f, setF] = useState(() =>
    template
      ? {
          ...blank,
          ...template,
          description: template.description || "",
          titleAr: template.titleAr || "",
          bodyAr: template.bodyAr || "",
          variables: Array.isArray(template.variables) ? template.variables : [],
          defaultDurationHours: template.defaultDurationHours ?? "",
          defaultAudienceId: template.defaultAudienceId ?? "",
        }
      : blank
  );
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const used = useMemo(() => extractPlaceholders(f.titleEn, f.titleAr, f.bodyEn, f.bodyAr), [f.titleEn, f.titleAr, f.bodyEn, f.bodyAr]);
  const definedKeys = new Set(f.variables.map((v) => v.key));
  const undefinedKeys = used.filter((k) => !definedKeys.has(k));

  const addVariable = (key = "") =>
    set({
      variables: [
        ...f.variables,
        { key, labelEn: key ? key.replace(/([A-Z])/g, " $1").replace(/^./, (ch) => ch.toUpperCase()) : "", labelAr: "", type: "text", required: true },
      ],
    });
  const updateVar = (i, patch) => set({ variables: f.variables.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });
  const removeVar = (i) => set({ variables: f.variables.filter((_, idx) => idx !== i) });

  const badVar = f.variables.find((v) => !/^[a-zA-Z0-9_]{1,40}$/.test(v.key) || !v.labelEn.trim());
  const canSave = f.name.trim() && f.titleEn.trim() && f.bodyEn.trim() && !badVar && undefinedKeys.length === 0;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        description: f.description || null,
        category: f.category,
        titleEn: f.titleEn,
        titleAr: f.titleAr || null,
        bodyEn: f.bodyEn,
        bodyAr: f.bodyAr || null,
        variables: f.variables.map((v) => ({ ...v, labelAr: v.labelAr || null })),
        severity: f.severity,
        displayMode: f.displayMode,
        dismissible: f.dismissible,
        requireAck: f.requireAck,
        addToInbox: f.addToInbox,
        defaultDurationHours: f.defaultDurationHours === "" ? null : Number(f.defaultDurationHours),
        defaultAudienceId: f.defaultAudienceId === "" ? null : Number(f.defaultAudienceId),
      };
      if (template?.id) await broadcastApi.updateTemplate(template.id, payload);
      else await broadcastApi.createTemplate(payload);
      onSaved?.();
      notify("success", C.presetSaved(payload.name));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const mobile = screenSize.isMobile;
  const varCols = mobile
    ? "minmax(0, 1fr) minmax(0, 1fr)"
    : "minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.9fr) auto auto";

  const variant = { BANNER: "banner", TOAST: "toast", MODAL: "modal" }[f.displayMode];
  const previewBroadcast = {
    id: 0,
    titleEn: f.titleEn,
    titleAr: f.titleAr,
    bodyEn: f.bodyEn,
    bodyAr: f.bodyAr,
    severity: f.severity,
    displayMode: f.displayMode,
    dismissible: f.requireAck ? false : f.displayMode === "MODAL" ? true : f.dismissible,
    requireAck: f.requireAck,
  };
  const previewPane = (lang) => (
    <div style={{ marginBottom: 12 }}>
      <div style={s.previewLabel}>{lang === "en" ? C.english : C.arabic}</div>
      <div style={{ ...s.previewStage(variant), justifyContent: variant === "toast" ? (lang === "ar" ? "flex-end" : "flex-start") : "center" }}>
        <div style={{ width: variant === "toast" ? "min(340px, 100%)" : "100%" }}>
          <BroadcastCard broadcast={previewBroadcast} language={lang} theme={theme} variant={variant} preview />
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      title={template?.id ? E.editTitle(template.name) : E.newTitle}
      icon={LayoutTemplate}
      onClose={onClose}
      busy={busy}
      maxWidth={860}
      footer={
        <>
          <Btn kind="secondary" onClick={onClose} disabled={busy}>
            {t.cancel}
          </Btn>
          <Btn disabled={busy || !canSave} onClick={save}>
            <Save size={16} /> {busy ? t.saving : E.savePreset}
          </Btn>
        </>
      }
    >
      {/* ── Basics ── */}
      <GroupTitle icon={Info} style={{ marginTop: 8 }}>
        {E.basics}
      </GroupTitle>
      <div style={s.grid2}>
        <Field label={`${E.name} *`} hint={E.nameHint}>
          <input className="bc-input" style={s.input} value={f.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label={C.category}>
          <Select value={f.category} onChange={(v) => set({ category: v })} options={CATEGORIES.map((k) => ({ value: k, label: t.categories[k] }))} />
        </Field>
      </div>
      <Field label={E.description} hint={E.descriptionHint}>
        <input className="bc-input" style={s.input} value={f.description} maxLength={500} onChange={(e) => set({ description: e.target.value })} />
      </Field>

      {/* ── Message text ── */}
      <GroupTitle icon={FileText}>{E.content}</GroupTitle>
      <div style={{ ...s.notice("info"), alignItems: "flex-start", marginBottom: 18 }}>
        <Lightbulb size={16} style={{ flexShrink: 0, marginTop: 3 }} />
        <span>
          {E.contentHint}
          <span dir="auto" style={{ display: "block", marginTop: 4, fontWeight: 700 }}>
            {E.contentExample}
          </span>
        </span>
      </div>
      <div style={s.grid2}>
        <Field label={`${C.titleEn} *`}>
          <input dir="ltr" className="bc-input" style={s.input} value={f.titleEn} maxLength={200} onChange={(e) => set({ titleEn: e.target.value })} />
        </Field>
        <Field label={C.titleAr}>
          <input dir="rtl" className="bc-input" style={{ ...s.input, textAlign: "right" }} value={f.titleAr} maxLength={200} onChange={(e) => set({ titleAr: e.target.value })} />
        </Field>
        <Field label={`${C.bodyEn} *`}>
          <textarea dir="ltr" className="bc-input" style={s.textarea} value={f.bodyEn} maxLength={5000} onChange={(e) => set({ bodyEn: e.target.value })} />
        </Field>
        <Field label={C.bodyAr}>
          <textarea dir="rtl" className="bc-input" style={{ ...s.textarea, textAlign: "right" }} value={f.bodyAr} maxLength={5000} onChange={(e) => set({ bodyAr: e.target.value })} />
        </Field>
      </div>

      {/* ── Placeholders ── */}
      <GroupTitle
        icon={Braces}
        action={
          <Btn kind="secondary" small onClick={() => addVariable()}>
            <Plus size={14} /> {E.add}
          </Btn>
        }
      >
        {E.placeholders}
      </GroupTitle>
      <p style={{ ...s.hint, marginTop: -6, marginBottom: 14 }}>{E.placeholdersHint}</p>
      {undefinedKeys.length > 0 && (
        <div style={{ ...s.notice("warning"), flexWrap: "wrap", marginBottom: 14 }}>
          <span>{E.usedNotDefined}</span>
          {undefinedKeys.map((k) => (
            <button key={k} type="button" className="bc-action-btn" style={s.actionBtn("primary")} onClick={() => addVariable(k)}>
              <Plus size={12} /> {`{{${k}}}`}
            </button>
          ))}
        </div>
      )}
      {f.variables.length === 0 && undefinedKeys.length === 0 && (
        <div style={{ ...s.emptyState, padding: "18px 12px" }}>
          <p style={{ ...s.hint, margin: 0 }}>{E.noPlaceholders}</p>
        </div>
      )}
      {f.variables.length > 0 && !mobile && (
        <div style={{ display: "grid", gridTemplateColumns: varCols, gap: 8, marginBottom: 6 }}>
          {[E.colKey, E.colLabelEn, E.colLabelAr, E.colType, t.required, ""].map((h, i) => (
            <span key={i} style={{ ...s.label, marginBottom: 0 }}>
              {h}
            </span>
          ))}
        </div>
      )}
      {f.variables.map((v, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: varCols, gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input
            dir="ltr"
            className="bc-input"
            style={s.input}
            placeholder="version"
            value={v.key}
            onChange={(e) => updateVar(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
            aria-label={E.colKey}
          />
          <input className="bc-input" dir="ltr" style={s.input} placeholder="Version" value={v.labelEn} onChange={(e) => updateVar(i, { labelEn: e.target.value })} aria-label={E.colLabelEn} />
          <input
            dir="rtl"
            className="bc-input"
            style={{ ...s.input, textAlign: "right" }}
            placeholder="رقم الإصدار"
            value={v.labelAr || ""}
            onChange={(e) => updateVar(i, { labelAr: e.target.value })}
            aria-label={E.colLabelAr}
          />
          <Select value={v.type} onChange={(val) => updateVar(i, { type: val })} options={VARIABLE_TYPES.map((k) => ({ value: k, label: t.variableTypes[k] }))} />
          <label style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "center", justifyContent: "center", color: s.colors.muted, whiteSpace: "nowrap", cursor: "pointer" }}>
            <input type="checkbox" className="bc-check" checked={v.required} onChange={(e) => updateVar(i, { required: e.target.checked })} />
            {mobile ? t.required : null}
          </label>
          <IconBtn icon={Trash2} label={E.removePlaceholder} tone="danger" onClick={() => removeVar(i)} />
        </div>
      ))}

      {/* ── How it shows ── */}
      <GroupTitle icon={SlidersHorizontal}>{E.defaults}</GroupTitle>
      <Field label={C.displayAs} hint={t.displayModes[f.displayMode].hint}>
        <Segmented value={f.displayMode} onChange={(v) => set({ displayMode: v })} options={DISPLAY_MODES.map((m) => ({ value: m, label: t.displayModes[m].label }))} />
      </Field>
      <div style={s.grid2}>
        <Field label={C.severity}>
          <Select value={f.severity} onChange={(v) => set({ severity: v })} options={SEVERITIES.map((k) => ({ value: k, label: t.severities[k] }))} />
        </Field>
        <Field label={E.showForHours} hint={E.showForHint}>
          <input
            type="number"
            min={1}
            max={8760}
            className="bc-input"
            style={s.input}
            value={f.defaultDurationHours}
            onChange={(e) => set({ defaultDurationHours: e.target.value })}
          />
        </Field>
      </div>
      <Field label={E.defaultAudience}>
        <Select
          value={f.defaultAudienceId}
          onChange={(v) => set({ defaultAudienceId: v ?? "" })}
          placeholder={t.none}
          options={[{ value: "", label: t.none }, ...audiences.map((a) => ({ value: a.id, label: a.name }))]}
        />
      </Field>
      <div style={{ marginTop: 6 }}>
        <Toggle
          checked={f.requireAck}
          onChange={(v) => set({ requireAck: v, dismissible: v ? false : f.dismissible })}
          label={C.requireAck}
          hint={C.requireAckHint}
        />
        <Toggle
          checked={f.requireAck ? false : f.displayMode === "MODAL" ? true : f.dismissible}
          disabled={f.requireAck || f.displayMode === "MODAL"}
          onChange={(v) => set({ dismissible: v })}
          label={C.dismissible}
          hint={C.dismissibleHint}
        />
        <Toggle checked={f.addToInbox} onChange={(v) => set({ addToInbox: v })} label={C.addToInbox} hint={C.addToInboxHint} />
      </div>

      {/* ── Preview ── */}
      <GroupTitle icon={Eye}>{E.previewTitle}</GroupTitle>
      <p style={{ ...s.hint, marginTop: -6, marginBottom: 14 }}>{E.previewHint}</p>
      {previewPane("en")}
      {(f.titleAr || f.bodyAr) ? (
        previewPane("ar")
      ) : (
        <div style={s.notice("warning")}>{C.noArabicPreview}</div>
      )}
    </Modal>
  );
};

export default TemplateEditorModal;
